import type { ComponentPropsWithoutRef, ReactNode } from "react";
import type { Post } from "../../lib/posts";
import { slugify } from "../../lib/slug";
import { Ad300x250 } from "../ads/Ad300x250";
import { InfoBox } from "./InfoBox";
import { FAQ, FactList, ReferenceList, RelatedTerms, ShortAnswer, Timeline } from "./TemplateBlocks";

type ArticleBodyProps = {
  post: Post;
};

function textFromChildren(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(textFromChildren).join(" ");
  return "";
}

export function ArticleBody({ post }: ArticleBodyProps) {
  const adAfterParagraph = post.paragraphCount >= 2 ? 2 : 1;
  let paragraphIndex = 0;

  const components = {
    FAQ,
    FactList,
    InfoBox,
    ReferenceList,
    RelatedTerms,
    ShortAnswer,
    Timeline,
    h2: ({ children, ...props }: ComponentPropsWithoutRef<"h2">) => {
      const id = slugify(textFromChildren(children));
      return (
        <h2 {...props} id={id}>
          <a href={`#${id}`} aria-label={`Link to ${textFromChildren(children)}`}>
            #
          </a>
          {children}
        </h2>
      );
    },
    h3: ({ children, ...props }: ComponentPropsWithoutRef<"h3">) => {
      const id = slugify(textFromChildren(children));
      return (
        <h3 {...props} id={id}>
          <a href={`#${id}`} aria-label={`Link to ${textFromChildren(children)}`}>
            #
          </a>
          {children}
        </h3>
      );
    },
    p: ({ children, ...props }: ComponentPropsWithoutRef<"p">) => {
      paragraphIndex += 1;
      return (
        <>
          <p {...props}>{children}</p>
          {paragraphIndex === adAfterParagraph ? <Ad300x250 /> : null}
        </>
      );
    }
  };

  const Content = post.Component;

  return (
    <div className="article-body">
      <Content components={components} />
    </div>
  );
}
